import { useState, useRef, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useI18n } from "@/lib/i18n";
import {
  ArrowLeft, Globe, Check, Minus, Plus, Edit2, Trash2,
  Save, X, Loader2, Send, ChevronDown, AlertCircle,
  Image, Video, FileAudio, Upload, ExternalLink, Star, Bot,
} from "lucide-react";
import { AdminTopbar } from "@/components/admin/AdminLayout";
import { ModulePanel, EditorialStatusBadge } from "@/components/admin/AdminPrimitives";
import { RichTextEditor } from "@/components/admin/RichTextEditor";
import { MultiSelectDropdown } from "@/components/admin/MultiSelectDropdown";
import { ConfirmDeleteDialog } from "@/components/admin/ConfirmDeleteDialog";
import {
  useArticle, usePublishArticle, useUpdateArticle, useResoumettreArticle, useRefuserArticle,
  estBlocageDroitDeReponse,
} from "@/hooks/useArticles";
import { RefusArticleDialog, DerogationDroitReponseDialog } from "@/components/admin/DecisionEditorialeDialogs";
import { AlerteDroitDeReponse, NewsroomPanel, HistoriquePanel } from "@/components/admin/NewsroomPanels";
import { useUploadImage } from "@/hooks/useFileUpload";
import { useCategories } from "@/hooks/useCategories";
import { useEtiquettes } from "@/hooks/useEtiquettes";
import { useRubriques } from "@/hooks/useRubriques";
import { usePays } from "@/hooks/usePays";
import { useArticleStats } from "@/hooks/useAnalytics";
import { useCommentairesAdmin, useChangerStatutCommentaire, useDeleteCommentaire } from "@/hooks/useCommentaires";
import { useLikeCount } from "@/hooks/useLikes";
import {
  useTraductionsArticle,
  useCreateTraductionArticle,
  useUpdateTraductionArticle,
  useDeleteTraductionArticle,
} from "@/hooks/useTraductionArticle";
import { TraductionArticleDto } from "@/types/traduction-article.types";
import { MediaItem } from "@/types/article.types";
import { LANGUES_CONFIG, STATUT_MAP } from "@/lib/enums";
import { hasAnyRole, useBloquerPartenaire } from "@/pages/admin/RequireAdmin";
import { AudioPlayer } from "@/components/public/AudioPlayer";
import { S3UploadResponse } from "@/types/s3UploadResponse.types";
import FileUploadService from "@/integrations/service/FileUploadService.service";
import AuthService from "@/integrations/service/auth.service";


const LANGUES = LANGUES_CONFIG.map((l) => ({
  code: l.code,
  label: l.label,
  flag: l.flag,
  apiCode: l.apiCode,
}));

type TraductionFormFields = {
  titre: string;
  sousTitre: string;
  corps: string;
};

const emptyFields = (): TraductionFormFields => ({
  titre: "",
  sousTitre: "",
  corps: "",
});

function TraductionModal({
  articleId, langue, langLabel, initial, traductionId, frTraduction, onClose, onSaved,
}: {
  articleId: number;
  langue: string;
  langLabel: string;
  initial?: TraductionFormFields;
  traductionId?: number;
  frTraduction?: TraductionArticleDto;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useI18n();
  const createTraduction = useCreateTraductionArticle();
  const updateTraduction = useUpdateTraductionArticle();
  const isEdit = !!traductionId;
 
  const isFr = langue?.toUpperCase() === "FR";
  const showSplit = !isFr && !!frTraduction;

  const [fields, setFields] = useState<TraductionFormFields>(initial || emptyFields());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [lastAutoSaveAt, setLastAutoSaveAt] = useState<Date | null>(null);

  const set = (key: keyof TraductionFormFields, val: string) =>
    setFields((p) => ({ ...p, [key]: val }));

  // Enregistrement automatique : uniquement sur une traduction déjà créée
  // (une nouvelle traduction n'a pas encore d'id à mettre à jour), après 3s
  // d'inactivité, et seulement si le titre requis est renseigné.
  const skipNextAutoSave = useRef(true);
  const autoSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!isEdit) return;
    if (skipNextAutoSave.current) {
      skipNextAutoSave.current = false;
      return;
    }
    if (!fields.titre.trim()) return;
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(() => {
      updateTraduction.mutate(
        { id: traductionId!, payload: { langue, ...fields } },
        { onSuccess: () => setLastAutoSaveAt(new Date()) }
      );
    }, 3000);
    return () => {
      if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fields.titre, fields.sousTitre, fields.corps]);

  const handleSave = async () => {
    if (!fields.titre.trim()) { setError(t("admin.article.trad.titleRequired")); return; }
    setSaving(true);
    setError("");
    try {
      if (isEdit) {
        await updateTraduction.mutateAsync({ id: traductionId!, payload: { langue, ...fields } });
      } else {
        await createTraduction.mutateAsync({ articleId, payload: { langue, ...fields } });
      }
      onSaved();
      onClose();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || t("admin.common.saveError"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className={`w-full ${showSplit ? "max-w-5xl" : "max-w-2xl"} rounded-lg border border-border bg-background shadow-2xl flex flex-col max-h-[90vh]`}>
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <div>
            <h2 className="font-serif text-base font-semibold text-foreground">
              {isEdit ? t("admin.common.edit") : t("admin.common.add")} {t("admin.common.translations").toLowerCase()} — {langLabel}
            </h2>
            <p className="text-xs text-muted-foreground">
              {isEdit ? t("admin.article.trad.edit") : t("admin.article.trad.new")}
            </p>
            {lastAutoSaveAt && (
              <p className="text-[10px] text-verified mt-0.5">
                Enregistré automatiquement à {lastAutoSaveAt.toLocaleTimeString()}
              </p>
            )}
          </div>
          <button onClick={onClose} className="rounded-md p-1.5 hover:bg-surface text-muted-foreground hover:text-foreground transition-colors">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {error && (
            <div className="flex items-center gap-2 rounded-md bg-alert/10 border border-alert/30 px-4 py-2 text-xs text-alert font-medium">
              <AlertCircle className="h-3.5 w-3.5 shrink-0" />{error}
            </div>
          )}
          {showSplit && (
            <div className="rounded-md border border-border bg-surface/40 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              {t("admin.translations.split.hint")}
            </div>
          )}
          <div className={showSplit ? "grid gap-4 md:grid-cols-2" : "space-y-1.5"}>
            {showSplit && (
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-muted-foreground">{t("admin.article.titre")} — FR</label>
                <div className="w-full rounded-md border border-border bg-surface/50 px-3 py-2 text-sm text-foreground/80 min-h-[38px]">
                  {frTraduction?.titre || "—"}
                </div>
              </div>
            )}
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-muted-foreground">{t("admin.article.titre")} <span className="text-alert">*</span></label>
              <input value={fields.titre} onChange={(e) => set("titre", e.target.value)} placeholder="Titre de l'article" autoFocus
                className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-foreground" />
            </div>
          </div>
          <div className={showSplit ? "grid gap-4 md:grid-cols-2" : "space-y-1.5"}>
            {showSplit && (
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-muted-foreground">{t("admin.article.sousTitre")} — FR</label>
                <div className="w-full rounded-md border border-border bg-surface/50 px-3 py-2 text-sm text-foreground/80 min-h-[38px]">
                  {frTraduction?.sousTitre || "—"}
                </div>
              </div>
            )}
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-muted-foreground">{t("admin.article.sousTitre")}</label>
              <input value={fields.sousTitre} onChange={(e) => set("sousTitre", e.target.value)} placeholder="Sous-titre ou chapô"
                className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-foreground" />
            </div>
          </div>
          <div className={showSplit ? "grid gap-4 md:grid-cols-2" : "space-y-1.5"}>
            {showSplit && (
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-muted-foreground">{t("admin.article.corps")} — FR</label>
                <div
                  className="prose prose-sm max-w-none rounded-md border border-border bg-surface/50 px-3 py-2 text-foreground/80 min-h-[200px] max-h-[300px] overflow-y-auto"
                  dangerouslySetInnerHTML={{ __html: frTraduction?.corps || "" }}
                />
              </div>
            )}
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-muted-foreground">{t("admin.article.corps")}</label>
              <RichTextEditor value={fields.corps} onChange={(html) => set("corps", html)} placeholder="Contenu principal de l'article…" />
            </div>
          </div>
        </div>
        <div className="flex items-center justify-between border-t border-border px-6 py-4">
          <button onClick={onClose} className="rounded-md border border-border px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-surface transition-colors">
            {t("admin.common.cancel")}
          </button>
          <button onClick={handleSave} disabled={saving}
            className="flex items-center gap-2 rounded-md bg-foreground px-5 py-2 text-sm font-semibold text-background hover:bg-foreground/90 disabled:opacity-60 transition-colors">
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
            {t("admin.common.save")}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Carte langue ─────────────────────────────────────────────────────────────
function LangueCard({
  lang, trad, articleId, frTraduction, onRefresh,
}: {
  lang: typeof LANGUES[number];
  trad?: TraductionArticleDto;
  articleId: number;
  frTraduction?: TraductionArticleDto;
  onRefresh: () => void;
}) {
  const { t } = useI18n();
  const deleteTraduction = useDeleteTraductionArticle();
  const [formOpen, setFormOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const hasIt = !!trad;

  const handleDelete = () => {
    if (!trad?.id) return;
    setDeleting(true);
    deleteTraduction.mutate(trad.id, {
      onSuccess: () => { setConfirmOpen(false); onRefresh(); },
      onError: () => toast.error(t("admin.common.deleteError")),
      onSettled: () => setDeleting(false),
    });
  };

  return (
    <>
      <div className="rounded-md border border-border bg-background overflow-hidden">
        <div className="flex items-center gap-3 px-4 py-3">
          <span className="text-xl leading-none">{lang.flag}</span>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-foreground">{lang.label}</span>
              {hasIt ? (
                <span className="inline-flex items-center gap-1 rounded-sm px-2 py-0.5 text-[10px] font-bold bg-verified/10 text-verified">
                  <Check className="h-2.5 w-2.5" /> {t("admin.common.translated")}
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 rounded-sm px-2 py-0.5 text-[10px] font-bold bg-surface text-muted-foreground">
                  <Minus className="h-2.5 w-2.5" /> {t("admin.common.missing")}
                </span>
              )}
            </div>
            {trad?.titre && <p className="text-[11px] text-muted-foreground line-clamp-1 mt-0.5">{trad.titre}</p>}
          </div>
          <div className="flex items-center gap-1 shrink-0">
            {hasIt ? (
              <>
                <button onClick={() => setExpanded((p) => !p)} title="Voir le contenu"
                  className="rounded p-1 text-muted-foreground hover:text-foreground hover:bg-surface transition-colors">
                  <ChevronDown className={`h-3.5 w-3.5 transition-transform ${expanded ? "rotate-180" : ""}`} />
                </button>
                <button onClick={() => setFormOpen(true)} title="Modifier"
                  className="rounded p-1 text-muted-foreground hover:text-foreground hover:bg-surface transition-colors">
                  <Edit2 className="h-3.5 w-3.5" />
                </button>
                <button onClick={() => setConfirmOpen(true)} disabled={deleting} title="Supprimer cette traduction"
                  className="rounded p-1 text-muted-foreground hover:text-alert hover:bg-alert/10 transition-colors">
                  {deleting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                </button>
              </>
            ) : (
              <button onClick={() => setFormOpen(true)}
                className="flex items-center gap-1 rounded-md border border-dashed border-border px-2.5 py-1 text-xs font-semibold text-muted-foreground hover:text-foreground hover:border-foreground transition-colors">
                <Plus className="h-3 w-3" /> {t("admin.common.add")}
              </button>
            )}
          </div>
        </div>
        {expanded && trad && (
          <div className="border-t border-border bg-surface/30 px-4 py-4 space-y-3">
            {trad.sousTitre && <div><p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">{t("admin.detail.article.meta.subtitle")}</p><p className="text-sm text-foreground">{trad.sousTitre}</p></div>}
            {trad.corps && (
              <div>
                <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">{t("admin.detail.article.meta.body")}</p>
                <p className="text-sm text-foreground whitespace-pre-wrap font-mono text-[11px] leading-relaxed line-clamp-6">{trad.corps}</p>
              </div>
            )}
            {!trad.sousTitre && !trad.corps && (
              <p className="text-xs text-muted-foreground italic">{t("admin.detail.article.onlyTitle")}</p>
            )}
          </div>
        )}
      </div>
      {formOpen && (
        <TraductionModal
          articleId={articleId}
          langue={lang.apiCode}
          langLabel={lang.label}
          initial={trad ? { titre: trad.titre || "", sousTitre: trad.sousTitre || "", corps: trad.corps || "" } : undefined}
          traductionId={trad?.id}
          frTraduction={frTraduction}
          onClose={() => setFormOpen(false)}
          onSaved={onRefresh}
        />
      )}
      <ConfirmDeleteDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={t("admin.translations.deleteConfirm.title")}
        description={t("admin.translations.deleteConfirm.desc")}
        onConfirm={handleDelete}
        loading={deleting}
      />
    </>
  );
}

// ─── Icône et label par type de media ────────────────────────────────────────
const MEDIA_META: Record<string, { icon: any; label: string; color: string }> = {
  image: { icon: Image, label: "Image", color: "bg-blue-500/10 text-blue-500" },
  video: { icon: Video, label: "Vidéo", color: "bg-purple-500/10 text-purple-500" },
  audio: { icon: FileAudio, label: "Audio", color: "bg-amber-500/10 text-amber-500" },
};

// ─── Carte média individuelle ─────────────────────────────────────────────────
function MediaCard({
  media,
  onDelete,
  imageCouverture,
  onSetCover,
}: {
  media: MediaItem;
  onDelete: (key: string) => void;
  imageCouverture?: string;
  onSetCover?: (url: string) => void;
}) {
  const meta = MEDIA_META[media.type] || MEDIA_META["image"];
  const Icon = meta.icon;
  const isCover = media.type === "image" && media.url === imageCouverture;

  return (
    <div className={`group rounded-md border bg-background overflow-hidden ${isCover ? "border-amber-500/60" : "border-border"}`}>
      {/* Prévisualisation */}
      {media.type === "image" && (
        <div className="relative aspect-video bg-surface overflow-hidden">
          <img src={media.url} alt="" className="w-full h-full object-cover" />
          {isCover && (
            <div className="absolute top-2 left-2 flex items-center gap-1 rounded-full bg-amber-500 px-2 py-0.5 text-[10px] font-semibold text-white">
              <Star className="h-2.5 w-2.5 fill-white" /> Couverture
            </div>
          )}
        </div>
      )}
      {media.type === "video" && (
        <div className="aspect-video bg-surface overflow-hidden">
          <video src={media.url} className="w-full h-full object-cover" />
        </div>
      )}
      {media.type === "audio" && (
        <div className="p-3 bg-surface/50">
          <AudioPlayer title={media.s3Key.split("/").pop()} src={media.url} />
        </div>
      )}

      {/* Footer carte */}
      <div className="flex items-center gap-2 px-3 py-2.5">
        <div className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md ${meta.color}`}>
          <Icon className="h-3 w-3" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium text-foreground truncate">{media.s3Key.split("/").pop()}</p>
          <p className="text-[10px] text-muted-foreground font-mono truncate">{media.s3Key}</p>
        </div>
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          {media.type === "image" && onSetCover && (
            <button
              onClick={() => onSetCover(media.url)}
              title={isCover ? "Image de couverture actuelle" : "Définir comme couverture"}
              className={`rounded p-1 transition-colors ${isCover ? "text-amber-500" : "text-muted-foreground hover:text-amber-500 hover:bg-amber-500/10"}`}
            >
              <Star className="h-3.5 w-3.5" fill={isCover ? "currentColor" : "none"} />
            </button>
          )}
          <a href={media.url} target="_blank" rel="noopener noreferrer"
            title="Ouvrir dans un onglet"
            className="rounded p-1 text-muted-foreground hover:text-foreground hover:bg-surface transition-colors">
            <ExternalLink className="h-3.5 w-3.5" />
          </a>
          <button onClick={() => onDelete(media.s3Key)} title="Supprimer ce média"
            className="rounded p-1 text-muted-foreground hover:text-alert hover:bg-alert/10 transition-colors">
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Image de couverture (indépendante de la liste des médias) ───────────────
function CoverImagePanel({
  articleId,
  imageCouverture,
  onRefresh,
}: {
  articleId: number;
  imageCouverture?: string;
  onRefresh: () => void;
}) {
  const { t } = useI18n();
  const updateArticle = useUpdateArticle();
  const uploadImage = useUploadImage();
  const fileRef = useRef<HTMLInputElement>(null);
  const [imgError, setImgError] = useState(false);
  const [error, setError] = useState("");

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setError("");
    try {
      const result = await uploadImage.mutateAsync(file);
      setImgError(false);
      await updateArticle.mutateAsync({ id: articleId, payload: { imageCouverture: result.url } });
      onRefresh();
    } catch {
      setError(t("admin.common.uploadError"));
    }
  };

  const handleRemove = async () => {
    try {
      // Le backend ignore les champs `null` (pas de distinction "omis" vs "explicitement vidé"),
      // on envoie donc une chaîne vide pour effacer réellement la couverture.
      await updateArticle.mutateAsync({ id: articleId, payload: { imageCouverture: "" } });
      onRefresh();
    } catch {
      setError(t("admin.common.saveError"));
    }
  };

  return (
    <ModulePanel title={t("admin.article.coverImage")}>
      {error && (
        <div className="mb-3 flex items-center gap-2 rounded-md bg-alert/10 border border-alert/30 px-3 py-2 text-xs text-alert font-medium">
          <AlertCircle className="h-3.5 w-3.5 shrink-0" />{error}
        </div>
      )}
      {imageCouverture ? (
        <div className="relative">
          {imgError ? (
            <div className="aspect-video w-full space-y-2 overflow-hidden rounded-md border border-dashed border-alert/40 bg-alert/5 p-4 text-xs text-alert">
              <p className="font-semibold">
                {t("admin.detail.article.coverImage.loadError")}
              </p>
              <a href={imageCouverture} target="_blank" rel="noreferrer" className="block break-all underline">
                {imageCouverture}
              </a>
            </div>
          ) : (
            <img
              src={imageCouverture}
              alt=""
              className="aspect-video w-full rounded-md object-cover"
              onError={() => setImgError(true)}
            />
          )}
          <button
            type="button"
            onClick={handleRemove}
            className="absolute right-2 top-2 rounded-full bg-background/90 p-1 text-foreground hover:bg-background"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      ) : (
        <label className="flex cursor-pointer flex-col items-center gap-2 rounded-md border border-dashed border-border bg-surface/40 px-5 py-6 text-muted-foreground hover:border-foreground hover:text-foreground hover:bg-surface transition-colors">
          {uploadImage.isPending ? <Loader2 className="h-5 w-5 animate-spin" /> : <Image className="h-5 w-5" />}
          <span className="text-xs font-semibold">{t("admin.article.chooseCover")}</span>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleUpload} disabled={uploadImage.isPending} />
        </label>
      )}
    </ModulePanel>
  );
}

// ─── Section médias dans le détail article ────────────────────────────────────
function MediasPanel({
  articleId,
  medias,
  imageCouverture,
  onRefresh,
}: {
  articleId: number;
  medias: MediaItem[];
  imageCouverture?: string;
  onRefresh: () => void;
}) {
  const { t } = useI18n();
  const updateArticle = useUpdateArticle();
  const imageRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLInputElement>(null);
  const audioRef = useRef<HTMLInputElement>(null);

  const [uploading, setUploading] = useState<{ name: string; type: string } | null>(null);
  const [uploadError, setUploadError] = useState("");
  const [deleteKey, setDeleteKey] = useState<string | null>(null);
  const [deletingMedia, setDeletingMedia] = useState(false);

  const upload = async (file: File, type: "image" | "video" | "audio") => {
    setUploading({ name: file.name, type });
    setUploadError("");
    try {
      let result: S3UploadResponse;
      if (type === "image") result = await FileUploadService.uploadImage(file);
      else if (type === "video") result = await FileUploadService.uploadVideo(file);
      else result = await FileUploadService.uploadDocument(file);

      const newMedia: MediaItem = { s3Key: result.s3Key, url: result.url, type };
      await updateArticle.mutateAsync({
        id: articleId,
        payload: { medias: [...medias, newMedia] },
      });
      onRefresh();
    } catch (e: any) {
      setUploadError(e?.message || t("admin.common.uploadError"));
    } finally {
      setUploading(null);
    }
  };

  const confirmDeleteMedia = async () => {
    if (!deleteKey) return;
    setDeletingMedia(true);
    try {
      await FileUploadService.delete(deleteKey);
      const newMedias = medias.filter((m) => m.s3Key !== deleteKey);
      const deletedUrl = medias.find((m) => m.s3Key === deleteKey)?.url;
      const payload: any = { medias: newMedias };
      if (deletedUrl && deletedUrl === imageCouverture) {
        // "" plutôt que null : le backend ignore les champs null (pas de distinction
        // "omis" vs "explicitement vidé"), donc null ne viderait pas réellement la couverture.
        payload.imageCouverture = newMedias.find((m) => m.type === "image")?.url ?? "";
      }
      await updateArticle.mutateAsync({ id: articleId, payload });
      setDeleteKey(null);
      onRefresh();
    } catch (e: any) {
      setUploadError(e?.message || t("admin.common.deleteError"));
    } finally {
      setDeletingMedia(false);
    }
  };

  const handleSetCover = async (url: string) => {
    try {
      await updateArticle.mutateAsync({ id: articleId, payload: { imageCouverture: url } });
      onRefresh();
    } catch (e: any) {
      setUploadError(e?.message || "Erreur lors de la définition de la couverture");
    }
  };

  return (
    <ModulePanel
      title="Médias"
      description={`${medias.length} fichier${medias.length !== 1 ? "s" : ""} attaché${medias.length !== 1 ? "s" : ""}`}
    >
      {/* Erreur upload */}
      {uploadError && (
        <div className="flex items-center gap-2 rounded-md bg-alert/10 border border-alert/30 px-4 py-2 text-xs text-alert font-medium mb-4">
          <AlertCircle className="h-3.5 w-3.5 shrink-0" />{uploadError}
        </div>
      )}

      {/* Boutons d'ajout */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
        {/* Image */}
        <input ref={imageRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden"
          onChange={(e) => e.target.files?.[0] && upload(e.target.files[0], "image")} />
        <button onClick={() => imageRef.current?.click()} disabled={!!uploading}
          className="flex flex-col items-center gap-2 rounded-md border border-dashed border-border bg-surface/40 px-5 py-4 text-muted-foreground hover:border-foreground hover:text-foreground hover:bg-surface transition-colors disabled:opacity-50">
          <Image className="h-5 w-5" />
          <span className="text-xs font-semibold">Image</span>
        </button>

        {/* Vidéo */}
        <input ref={videoRef} type="file" accept="video/mp4,video/webm,video/quicktime" className="hidden"
          onChange={(e) => e.target.files?.[0] && upload(e.target.files[0], "video")} />
        <button onClick={() => videoRef.current?.click()} disabled={!!uploading}
          className="flex flex-col items-center gap-2 rounded-md border border-dashed border-border bg-surface/40 px-5 py-4 text-muted-foreground hover:border-foreground hover:text-foreground hover:bg-surface transition-colors disabled:opacity-50">
          <Video className="h-5 w-5" />
          <span className="text-xs font-semibold">Vidéo</span>
        </button>

        {/* Audio */}
        <input ref={audioRef} type="file" accept="audio/mpeg,audio/ogg,audio/wav,audio/mp4" className="hidden"
          onChange={(e) => e.target.files?.[0] && upload(e.target.files[0], "audio")} />
        <button onClick={() => audioRef.current?.click()} disabled={!!uploading}
          className="flex flex-col items-center gap-2 rounded-md border border-dashed border-border bg-surface/40 px-5 py-4 text-muted-foreground hover:border-foreground hover:text-foreground hover:bg-surface transition-colors disabled:opacity-50">
          <FileAudio className="h-5 w-5" />
          <span className="text-xs font-semibold">Audio</span>
        </button>
      </div>

      {/* Upload en cours */}
      {uploading && (
        <div className="flex items-center gap-3 rounded-md border border-border bg-surface/40 px-4 py-3 mb-4">
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground shrink-0" />
          <div className="min-w-0">
            <p className="text-xs font-medium text-foreground truncate">{uploading.name}</p>
            <p className="text-[10px] text-muted-foreground">{t("admin.common.sendToS3")}</p>
          </div>
        </div>
      )}

      {/* Grille de médias */}
      {medias.length > 0 ? (
        <div className="grid gap-3 sm:grid-cols-2">
          {medias.map((m, i) => (
            <MediaCard
              key={m.s3Key + i}
              media={m}
              onDelete={setDeleteKey}
              imageCouverture={imageCouverture}
              onSetCover={handleSetCover}
            />
          ))}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-8 gap-2 text-muted-foreground">
          <Upload className="h-8 w-8 opacity-20" />
          <p className="text-xs">{t("admin.detail.article.noMedia")}</p>
        </div>
      )}

      <ConfirmDeleteDialog
        open={!!deleteKey}
        onOpenChange={(open) => !open && setDeleteKey(null)}
        title={t("admin.detail.article.media.deleteConfirm.title")}
        description={t("admin.detail.article.media.deleteConfirm.desc")}
        onConfirm={confirmDeleteMedia}
        loading={deletingMedia}
      />
    </ModulePanel>
  );
}

// ─── Édition rubrique / catégories / étiquettes / SEO / dates ────────────────
function MetadataEditForm({
  article,
  articleId,
  onSaved,
}: {
  article: any;
  articleId: number;
  onSaved: () => void;
}) {
  const { t, locale } = useI18n();
  const updateArticle = useUpdateArticle();
  const { data: categories } = useCategories();
  const { data: etiquettes } = useEtiquettes();
  const { data: rubriques } = useRubriques();
  const { data: pays } = usePays();
  const categoryList = Array.isArray(categories) ? categories : [];
  const etiquetteList = Array.isArray(etiquettes) ? etiquettes : [];
  const rubriqueList = Array.isArray(rubriques) ? rubriques : [];
  const paysList = Array.isArray(pays) ? pays : [];

  const toLocalInput = (iso?: string) => {
    if (!iso) return "";
    const d = new Date(iso);
    d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
    return d.toISOString().slice(0, 16);
  };

  const [rubriqueId, setRubriqueId] = useState(article.rubriqueId != null ? String(article.rubriqueId) : "");
  const [paysIds, setPaysIds] = useState<number[]>(article.paysIds ?? []);
  const [categorieIds, setCategorieIds] = useState<number[]>(article.categorieIds ?? []);
  const [etiquetteIds, setEtiquetteIds] = useState<number[]>((article.etiquettes ?? []).map((e: any) => e.id));
  const [metaTitre, setMetaTitre] = useState(article.metaTitre || "");
  const [metaDescription, setMetaDescription] = useState(article.metaDescription || "");
  const [creeLe, setCreeLe] = useState(toLocalInput(article.creeLe));
  const [publieLe, setPublieLe] = useState(toLocalInput(article.publieLe));
  const [modifieLe, setModifieLe] = useState(toLocalInput(article.modifieLe));
  const [saving, setSaving] = useState(false);
  const [lastAutoSaveAt, setLastAutoSaveAt] = useState<Date | null>(null);
  const hasRole = AuthService.hasRole("ADMIN");

  const construirePayload = () => ({
    rubriqueId: rubriqueId ? Number(rubriqueId) : undefined,
    paysIds,
    categorieIds,
    etiquetteIds,
    metaTitre: metaTitre.trim() || undefined,
    metaDescription: metaDescription.trim() || undefined,
    // Un article programmé n'a pas de date de création/modification "réelle"
    // tant qu'il n'est pas effectivement publié — le backend les fixe lui-même
    // à ce moment-là, ne pas les écraser depuis ce formulaire.
    creeLe: article.statut === "PROGRAMME" || !creeLe ? undefined : new Date(creeLe).toISOString(),
    modifieLe: article.statut === "PROGRAMME" || !modifieLe ? undefined : new Date(modifieLe).toISOString(),
    publieLe: article.statut === "PROGRAMME" || !publieLe ? undefined : new Date(publieLe).toISOString(),
  });

  const handleSave = () => {
    setSaving(true);
    updateArticle.mutate(
      { id: articleId, payload: construirePayload() },
      {
        onSuccess: () => { toast.success(t("admin.common.saved")); onSaved(); },
        onError: () => toast.error(t("admin.common.saveError")),
        onSettled: () => setSaving(false),
      }
    );
  };

  // Enregistrement automatique après 3s d'inactivité sur rubrique/catégories/
  // étiquettes/SEO — pas de toast, juste un indicateur discret, pour ne pas
  // interrompre la saisie ni renaviguer comme le fait le bouton "Enregistrer".
  const skipNextAutoSave = useRef(true);
  const autoSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (skipNextAutoSave.current) {
      skipNextAutoSave.current = false;
      return;
    }
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(() => {
      updateArticle.mutate(
        { id: articleId, payload: construirePayload() },
        { onSuccess: () => setLastAutoSaveAt(new Date()) }
      );
    }, 3000);
    return () => {
      if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rubriqueId, paysIds, categorieIds, etiquetteIds, metaTitre, metaDescription]);

  return (
    <div className="mt-4 pt-4 border-t border-border space-y-4">
      {lastAutoSaveAt && (
        <p className="text-[10px] text-verified">
          Enregistré automatiquement à {lastAutoSaveAt.toLocaleTimeString()}
        </p>
      )}
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t("admin.article.rubrique")}</label>
          <select
            value={rubriqueId}
            onChange={(e) => setRubriqueId(e.target.value)}
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground"
          >
            <option value="">{t("admin.article.selectRubrique")}</option>
            {rubriqueList.map((r: any) => (
              <option key={r.id} value={r.id}>{locale === "fr" ? r.nom : (r.nomEn || r.nom)}</option>
            ))}
          </select>
        </div>
        <div className="space-y-1.5">
          <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Pays</label>
          <MultiSelectDropdown
            options={paysList.map((p: any) => ({ value: p.id, label: locale === "fr" ? p.nom : (p.nomEn || p.nom) }))}
            selected={paysIds}
            onChange={setPaysIds}
            placeholder={t("admin.article.select")}
          />
        </div>
        <div className="space-y-1.5">
          <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t("admin.article.categories")}</label>
          <MultiSelectDropdown
            options={categoryList.map((c: any) => ({ value: c.id, label: locale === "fr" ? c.libelleFr : c.libelleEn }))}
            selected={categorieIds}
            onChange={setCategorieIds}
            placeholder={t("admin.article.select")}
          />
        </div>
        <div className="space-y-1.5">
          <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t("admin.article.tags")}</label>
          <MultiSelectDropdown
            options={etiquetteList.map((et: any) => ({ value: et.id, label: locale === "fr" ? et.libelleFr : et.libelleEn }))}
            selected={etiquetteIds}
            onChange={setEtiquetteIds}
            placeholder={t("admin.article.select")}
          />
        </div>
        {article.statut === "PROGRAMME" ? (
  <p className="sm:col-span-1 rounded-md bg-background px-3 py-2 text-[11px] text-muted-foreground content-start">
    Les dates de création et de modification seront fixées automatiquement à la publication réelle
    {article.publieLe
      ? `, le ${new Date(article.publieLe).toLocaleString("fr-FR")}`
      : ""}.
  </p>
) : (
  hasRole && (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 content-start sm:col-span-2">
      
      {/* Date de création */}
      <div className="space-y-1.5">
        <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          {t("admin.article.dateCreation")}
        </label>

        <input
          type="datetime-local"
          value={creeLe}
          onChange={(e) => setCreeLe(e.target.value)}
          className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground"
        />
      </div>

      {/* Date de modification */}
      <div className="space-y-1.5">
        <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          {t("admin.article.dateModification")}
        </label>

        <input
          type="datetime-local"
          value={modifieLe}
          onChange={(e) => setModifieLe(e.target.value)}
          className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground"
        />
      </div>

      {/* Date de publication */}
      <div className="space-y-1.5">
        <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          {t("admin.article.datePublie")}
        </label>

        <input
          type="datetime-local"
          value={publieLe}
          onChange={(e) => setPublieLe(e.target.value)}
          className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground"
        />
      </div>

    </div>
  )
)}
        <div className="space-y-1.5 sm:col-span-2">
          <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t("admin.article.metaTitre")}</label>
          <input
            value={metaTitre}
            onChange={(e) => setMetaTitre(e.target.value)}
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground"
          />
        </div>
        <div className="space-y-1.5 sm:col-span-2">
          <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t("admin.article.metaDescription")}</label>
          <textarea
            value={metaDescription}
            onChange={(e) => setMetaDescription(e.target.value)}
            rows={2}
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground resize-none"
          />
        </div>
      </div>
      <button
        onClick={handleSave}
        disabled={saving}
        className="inline-flex items-center gap-2 rounded-md bg-foreground px-4 py-2 text-xs font-semibold text-background disabled:opacity-60"
      >
        {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
        {t("admin.common.save")}
      </button>
    </div>
  );
}

// ─── Modération des commentaires ──────────────────────────────────────────────
function CommentsModerationPanel({ articleId }: { articleId: number }) {
  const { t, locale } = useI18n();
  const { data: comments, isLoading } = useCommentairesAdmin(articleId);
  const changerStatut = useChangerStatutCommentaire(articleId);
  const deleteCommentaire = useDeleteCommentaire(articleId);
  const list = Array.isArray(comments) ? comments : [];

  return (
    <ModulePanel title={t("admin.detail.article.comments.title", { count: list.length })}>
      {isLoading ? (
        <div className="py-6 text-center text-sm text-muted-foreground">{t("admin.common.loading")}</div>
      ) : list.length === 0 ? (
        <div className="py-6 text-center text-sm text-muted-foreground">{t("admin.detail.article.comments.empty")}</div>
      ) : (
        <div className="space-y-2">
          {list.map((c: any) => (
            <div key={c.id} className="rounded-md border border-border bg-surface/40 px-3 py-2.5">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2 text-xs">
                  <span className="font-semibold text-foreground">{c.auteurNom}</span>
                  <span className="text-muted-foreground">
                    {new Date(c.creeLe).toLocaleString(locale === "fr" ? "fr-FR" : "en-GB")}
                  </span>
                  <span
                    className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${
                      c.statut === "APPROUVE"
                        ? "bg-verified/15 text-verified"
                        : c.statut === "REJETE"
                        ? "bg-alert/15 text-alert"
                        : "bg-amber-500/15 text-amber-600"
                    }`}
                  >
                    {c.statut}
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  {c.statut !== "APPROUVE" && (
                    <button
                      onClick={() => changerStatut.mutate({ id: c.id, statut: "APPROUVE" })}
                      title={t("admin.common.approve")}
                      className="rounded p-1 text-muted-foreground hover:text-verified hover:bg-verified/10 transition-colors"
                    >
                      <Check className="h-3.5 w-3.5" />
                    </button>
                  )}
                  {c.statut !== "REJETE" && (
                    <button
                      onClick={() => changerStatut.mutate({ id: c.id, statut: "REJETE" })}
                      title={t("admin.common.reject")}
                      className="rounded p-1 text-muted-foreground hover:text-alert hover:bg-alert/10 transition-colors"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  )}
                  <button
                    onClick={() => deleteCommentaire.mutate(c.id)}
                    title={t("admin.common.delete")}
                    className="rounded p-1 text-muted-foreground hover:text-alert hover:bg-alert/10 transition-colors"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
              <p className="mt-1.5 text-sm text-foreground">{c.contenu}</p>
            </div>
          ))}
        </div>
      )}
    </ModulePanel>
  );
}

// ─── Page Détail Article ──────────────────────────────────────────────────────
function DetailArticle() {
  useBloquerPartenaire();
  const { t, locale } = useI18n();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const articleId = Number(id);

  const { data: article, isLoading, refetch: refetchArticle } = useArticle(articleId);
  const { data: traductions, refetch: refetchTraductions } = useTraductionsArticle(articleId);
  const { data: categories } = useCategories();
  const { data: rubriques } = useRubriques();
  const { data: pays } = usePays();
  const { data: stats } = useArticleStats(articleId);
  const { data: likeStatut } = useLikeCount(articleId);
  const { data: commentsAdmin } = useCommentairesAdmin(articleId);
  const publishArticle = usePublishArticle();
  const updateArticle = useUpdateArticle();
  const resoumettreArticle = useResoumettreArticle();
  const refuserArticle = useRefuserArticle();

  const [publishing, setPublishing] = useState(false);
  const [refusOuvert, setRefusOuvert] = useState(false);
  const [derogationOuverte, setDerogationOuverte] = useState(false);

  // Un compte qui n'a que le rôle REDACTEUR ne peut jamais publier lui-même
  // (backend : POST /articles/{id}/publier réservé à ADMIN/EDITEUR) — le
  // bouton ne doit donc pas être affiché, sous peine d'un 403 trompeur.
  const estRedacteurSeul = hasAnyRole(["REDACTEUR"]) && !hasAnyRole(["ADMIN", "EDITEUR"]);

  // `forcer` : publication en dérogation quand un droit de réponse est encore attendu.
  const handlePublish = (forcer = false) => {
    setPublishing(true);
    publishArticle.mutate({ id: articleId, forcer }, {
      onSuccess: () => {
        setDerogationOuverte(false);
        refetchArticle();
      },
      onError: (e: any) => {
        if (estBlocageDroitDeReponse(e)) setDerogationOuverte(true);
        else toast.error(e?.response?.data?.message || t("admin.common.saveError"));
      },
      onSettled: () => setPublishing(false),
    });
  };

  const handleRefus = (commentaire: string, definitif: boolean) => {
    refuserArticle.mutate({ id: articleId, commentaire, definitif }, {
      onSuccess: () => {
        toast.success(definitif ? "Article rejeté et archivé" : "Révision demandée, le contributeur a été notifié");
        setRefusOuvert(false);
        refetchArticle();
      },
      onError: () => toast.error(t("admin.common.saveError")),
    });
  };

  const handleResoumettre = () => {
    resoumettreArticle.mutate(articleId, {
      onSuccess: () => {
        toast.success("Article resoumis, il repasse en relecture");
        refetchArticle();
      },
      onError: (e: any) => toast.error(e?.response?.data?.message || t("admin.common.saveError")),
    });
  };

  const handleRefresh = () => {
    refetchTraductions();
    refetchArticle();
  };

  const articleCategories = categories?.filter((c: any) => article?.categorieIds?.includes(c.id)) ?? [];
  const articleRubrique = (Array.isArray(rubriques) ? rubriques : []).find((r: any) => r.id === article?.rubriqueId);
  const articlePays = (Array.isArray(pays) ? pays : []).filter((p: any) => article?.paysIds?.includes(p.id));

  if (isLoading) {
    return (
      <>
        <AdminTopbar title={t("admin.common.metadata")} subtitle="" />
        <div className="flex-1 flex items-center justify-center gap-2 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" /><span className="text-sm">{t("admin.common.loading")}</span>
        </div>
      </>
    );
  }

  if (!article) {
    return (
      <>
        <AdminTopbar title={t("admin.detail.article.notFound")} subtitle="" />
        <div className="flex-1 flex flex-col items-center justify-center gap-3 text-muted-foreground">
          <AlertCircle className="h-8 w-8 opacity-40" />
          <p className="text-sm">{t("admin.detail.article.notExists")}</p>
          <button onClick={() => navigate(-1)} className="text-xs font-semibold text-foreground underline">{t("admin.detail.article.backLink")}</button>
        </div>
      </>
    );
  }

  // Pour un rédacteur, un "brouillon" soumis est déjà entre les mains de la
  // rédaction — "En attente" (de relecture) décrit mieux son état que
  // "Brouillon", qui laisse penser qu'il reste à écrire.
  const statutLabel = estRedacteurSeul && article.statut === "BROUILLON"
    ? "En attente"
    : STATUT_MAP[article.statut] || article.statut;

  const titreDisplay = article.traductions?.[0]?.titre || article.slug;
  const traductionsList: TraductionArticleDto[] = traductions || article.traductions || [];
  const completion = Math.round(((article.languesDisponibles?.length || 0) / LANGUES.length) * 100);
  const medias: MediaItem[] = article.medias || [];

  return (
    <>
      <AdminTopbar
        title={titreDisplay}
        subtitle={`Article · ${statutLabel}`}
      />

      <div className="flex-1 overflow-y-auto p-6 space-y-6">
        {/* Navigation + actions */}
        <div className="flex items-center justify-between">
          <button onClick={() => navigate(-1)}
            className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft className="h-3.5 w-3.5" /> {t("admin.detail.article.back")}
          </button>
          <div className="flex items-center gap-2">
            {/* Refus réservé à l'admin (backend : POST /articles/{id}/refuser). */}
            {hasAnyRole(["ADMIN"]) && ["BROUILLON", "RELECTURE", "JURIDIQUE"].includes(article.statut) && (
              <button onClick={() => setRefusOuvert(true)}
                className="flex items-center gap-1.5 rounded-md border border-alert px-3 py-2 text-xs font-semibold text-alert hover:bg-alert/10 transition-colors">
                <X className="h-3.5 w-3.5" /> Refuser
              </button>
            )}
            {article.statut !== "PUBLIE" && !estRedacteurSeul && (
              <button onClick={() => handlePublish()} disabled={publishing}
                className="flex items-center gap-1.5 rounded-md bg-verified px-3 py-2 text-xs font-semibold text-white hover:bg-verified/90 disabled:opacity-60 transition-colors">
                {publishing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                {t("admin.common.publish")}
              </button>
            )}
          </div>
        </div>

        {article.deposeParNewsroom && (
          <div className="flex items-center gap-2 rounded-md border border-border bg-surface px-4 py-3 text-sm">
            <Bot className="h-4 w-4 text-muted-foreground" />
            <span>
              Article déposé par la <span className="font-semibold">newsroom IA</span> : vérifiez les sources et la
              fiche de vérification avant toute publication.
            </span>
          </div>
        )}

        <AlerteDroitDeReponse newsroom={article.newsroom} />

        {article.statut === "ARCHIVE" && article.commentaireRefus && (
          <div className="rounded-md border border-alert/30 bg-alert/5 p-4 space-y-1">
            <div className="flex items-center gap-2 text-sm font-semibold text-alert">
              <AlertCircle className="h-4 w-4" /> Article rejeté
            </div>
            <p className="text-sm text-foreground/90 whitespace-pre-wrap">{article.commentaireRefus}</p>
          </div>
        )}

        {article.statut === "REFUSE" && (
          <div className="rounded-md border border-alert/30 bg-alert/5 p-4 space-y-3">
            <div className="flex items-center gap-2 text-sm font-semibold text-alert">
              <AlertCircle className="h-4 w-4" /> Article refusé
            </div>
            {article.commentaireRefus && (
              <p className="text-sm text-foreground/90 whitespace-pre-wrap">{article.commentaireRefus}</p>
            )}
            {article.deposeParNewsroom ? (
              // Seule la newsroom (son auteur) peut resoumettre : elle a été prévenue par webhook.
              <p className="text-xs text-muted-foreground">
                La newsroom IA a été notifiée ; l'article reviendra en relecture lorsqu'elle l'aura corrigé et resoumis.
              </p>
            ) : (
            <button
              onClick={handleResoumettre}
              disabled={resoumettreArticle.isPending}
              className="inline-flex items-center gap-2 rounded-md bg-foreground px-3 py-2 text-xs font-semibold text-background hover:opacity-90 disabled:opacity-60 transition-opacity"
            >
              {resoumettreArticle.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              Corriger et resoumettre
            </button>
            )}
          </div>
        )}

        {/* Métadonnées */}
        <ModulePanel title={t("admin.common.metadata")}>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">{t("admin.detail.article.meta.status")}</p>
              <EditorialStatusBadge status={statutLabel} />
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">{t("admin.common.col.type")}</p>
              <span className="font-mono text-sm text-foreground">{article.type}</span>
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">{t("admin.article.rubrique")}</p>
              <span className="text-sm text-foreground">
                {articleRubrique ? (locale === "fr" ? articleRubrique.nom : (articleRubrique.nomEn || articleRubrique.nom)) : "—"}
              </span>
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">{t("admin.detail.article.meta.category")}</p>
              <span className="text-sm text-foreground">
                {articleCategories.length
                  ? articleCategories.map((c: any) => (locale === "fr" ? c.libelleFr : c.libelleEn)).join(", ")
                  : "—"}
              </span>
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">Pays</p>
              <span className="text-sm text-foreground">
                {articlePays.length
                  ? articlePays.map((p: any) => (locale === "fr" ? p.nom : (p.nomEn || p.nom))).join(", ")
                  : "—"}
              </span>
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">{t("admin.detail.article.meta.signature")}</p>
              <span className="text-sm text-foreground">{article.signature || "—"}</span>
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">Auteur</p>
              <span className="text-sm text-foreground">{article.auteurNom || "—"}</span>
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">{t("admin.detail.article.meta.slug")}</p>
              <span className="font-mono text-xs text-muted-foreground">{article.slug}</span>
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">{t("admin.detail.article.meta.readingTime")}</p>
              <span className="text-sm text-foreground">{article.tempsLecture ? `${article.tempsLecture} min` : "—"}</span>
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">{t("admin.detail.article.meta.publishedAt")}</p>
              <span className="text-sm text-foreground">
                {article.publieLe ? new Date(article.publieLe).toLocaleDateString("fr-FR") : "—"}
              </span>
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">{t("admin.detail.article.meta.updatedAt")}</p>
              <span className="text-sm text-foreground">
                {article.modifieLe ? new Date(article.modifieLe).toLocaleDateString("fr-FR") : "—"}
              </span>
            </div>
          </div>

          <div className="mt-4 pt-4 border-t border-border grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">{t("admin.dashboard.audience.views")}</p>
              <span className="text-sm text-foreground">{stats?.vues ?? 0}</span>
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">{t("admin.dashboard.audience.reads")}</p>
              <span className="text-sm text-foreground">{stats?.lectures ?? 0}</span>
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">{t("admin.dashboard.audience.visitors")}</p>
              <span className="text-sm text-foreground">{stats?.visiteursUniques ?? 0}</span>
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">{t("admin.detail.article.comments.title", { count: "" })}</p>
              <span className="text-sm text-foreground">{commentsAdmin?.length ?? 0}</span>
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">{t("admin.detail.article.likes")}</p>
              <span className="text-sm text-foreground">{likeStatut?.total ?? 0}</span>
            </div>
          </div>

          <div className="mt-4 pt-4 border-t border-border">
            <label className="flex items-center gap-2 text-sm text-foreground">
              <input
                type="checkbox"
                checked={article.commentairesAutorises ?? true}
                onChange={(e) =>
                  updateArticle.mutate(
                    { id: articleId, payload: { commentairesAutorises: e.target.checked } },
                    { onSuccess: () => refetchArticle(), onError: () => toast.error(t("admin.common.saveError")) }
                  )
                }
              />
              {t("admin.article.commentsAllowed")}
            </label>
            <label className="mt-2 flex items-center gap-2 text-sm text-foreground">
              <input
                type="checkbox"
                checked={article.assisteParIa ?? false}
                onChange={(e) =>
                  updateArticle.mutate(
                    { id: articleId, payload: { assisteParIa: e.target.checked } },
                    { onSuccess: () => refetchArticle(), onError: () => toast.error(t("admin.common.saveError")) }
                  )
                }
              />
              {t("admin.article.aiAssisted")}
            </label>
            <label className="mt-2 flex items-center gap-2 text-sm text-foreground">
              <input
                type="checkbox"
                checked={article.subventionne ?? false}
                onChange={(e) =>
                  updateArticle.mutate(
                    { id: articleId, payload: { subventionne: e.target.checked } },
                    { onSuccess: () => refetchArticle(), onError: () => toast.error(t("admin.common.saveError")) }
                  )
                }
              />
              {t("admin.article.sponsored")}
            </label>
          </div>

          <MetadataEditForm article={article} articleId={articleId} onSaved={refetchArticle} />
        </ModulePanel>

        {article.newsroom && <NewsroomPanel newsroom={article.newsroom} />}

        {hasAnyRole(["ADMIN", "EDITEUR"]) && <HistoriquePanel articleId={articleId} />}

        {/* Image de couverture */}
        <CoverImagePanel
          articleId={articleId}
          imageCouverture={article.imageCouverture}
          onRefresh={refetchArticle}
        />

        {/* Médias */}
        <MediasPanel
          articleId={articleId}
          medias={medias}
          imageCouverture={article.imageCouverture}
          onRefresh={refetchArticle}
        />

        {/* Commentaires */}
        <CommentsModerationPanel articleId={articleId} />

        {/* Traductions */}
        <ModulePanel
          title={t("admin.common.translations")}
          description={t("admin.common.langues", { count: article.languesDisponibles?.length || 0, total: LANGUES.length, pct: completion })}
        >
          <div className="mb-5">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1">
                <Globe className="h-3 w-3" /> {t("admin.common.multilingual")}
              </span>
              <span className="text-[10px] font-bold text-muted-foreground">{completion}%</span>
            </div>
            <div className="h-1.5 rounded-full bg-surface overflow-hidden">
              <div className="h-full rounded-full bg-verified transition-all duration-500" style={{ width: `${completion}%` }} />
            </div>
          </div>
          <div className="space-y-2">
            {(() => {
              const frTrad = traductionsList.find((t) => t.langue?.toLowerCase() === "fr");
              return LANGUES.map((lang) => {
                const trad = traductionsList.find(
                  (t) => t.langue?.toLowerCase() === lang.code.toLowerCase()
                );
                return (
                  <LangueCard key={lang.code} lang={lang} trad={trad} articleId={articleId} frTraduction={frTrad} onRefresh={handleRefresh} />
                );
              });
            })()}
          </div>
        </ModulePanel>
      </div>

      <RefusArticleDialog
        titreArticle={titreDisplay}
        open={refusOuvert}
        onOpenChange={setRefusOuvert}
        onConfirm={handleRefus}
        loading={refuserArticle.isPending}
      />

      <DerogationDroitReponseDialog
        open={derogationOuverte}
        onOpenChange={setDerogationOuverte}
        onConfirm={() => handlePublish(true)}
        loading={publishing}
      />
    </>
  );
}

export default DetailArticle;

// import { useState } from "react";
// import { useParams, useNavigate } from "react-router-dom";
// import {
//   ArrowLeft, Globe, Check, Minus, Plus, Edit2, Trash2,
//   Save, X, Loader2, Send, ChevronDown, AlertCircle,
// } from "lucide-react";
// import { AdminTopbar } from "@/components/admin/AdminLayout";
// import { ModulePanel, EditorialStatusBadge } from "@/components/admin/AdminPrimitives";
// import { useArticle, usePublishArticle } from "@/hooks/useArticles";
// import { useCategories } from "@/hooks/useCategories";
// import {
//   useTraductionsArticle,
//   useCreateTraductionArticle,
//   useUpdateTraductionArticle,
//   useDeleteTraductionArticle,
// } from "@/hooks/useTraductionArticle";
// import { TraductionArticleDto } from "@/types/traduction-article.types";
// import { LANGUES_CONFIG, STATUT_MAP } from "@/lib/enums";

// // Les langues supportées (code frontend lowercase)
// const LANGUES = LANGUES_CONFIG.map((l) => ({
//   code: l.code,
//   label: l.label,
//   flag: l.flag,
//   apiCode: l.apiCode,
// }));

// type TraductionFormFields = {
//   titre: string;
//   sousTitre: string;
//   resume: string;
//   corps: string;
// };

// const emptyFields = (): TraductionFormFields => ({
//   titre: "",
//   sousTitre: "",
//   resume: "",
//   corps: "",
// });

// // ─── Modal formulaire traduction ──────────────────────────────────────────────
// function TraductionModal({
//   articleId,
//   langue,
//   langLabel,
//   initial,
//   traductionId,
//   onClose,
//   onSaved,
// }: {
//   articleId: number;
//   langue: string; // code API (ex: "FR")
//   langLabel: string;
//   initial?: TraductionFormFields;
//   traductionId?: number;
//   onClose: () => void;
//   onSaved: () => void;
// }) {
//   const createTraduction = useCreateTraductionArticle();
//   const updateTraduction = useUpdateTraductionArticle();
//   const isEdit = !!traductionId;

//   const [fields, setFields] = useState<TraductionFormFields>(initial || emptyFields());
//   const [saving, setSaving] = useState(false);
//   const [error, setError] = useState("");

//   const set = (key: keyof TraductionFormFields, val: string) =>
//     setFields((p) => ({ ...p, [key]: val }));

//   const handleSave = async () => {
//     if (!fields.titre.trim()) { setError("Le titre est requis."); return; }
//     setSaving(true);
//     setError("");
//     try {
//       if (isEdit) {
//         await updateTraduction.mutateAsync({
//           id: traductionId!,
//           payload: { langue, ...fields },
//         });
//       } else {
//         await createTraduction.mutateAsync({
//           articleId,
//           payload: { langue, ...fields },
//         });
//       }
//       onSaved();
//       onClose();
//     } catch (e: any) {
//       setError(e?.response?.data?.message || e?.message || "Erreur lors de l'enregistrement.");
//     } finally {
//       setSaving(false);
//     }
//   };

//   return (
//     <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
//       <div className="w-full max-w-2xl rounded-lg border border-border bg-background shadow-2xl flex flex-col max-h-[90vh]">
//         {/* Header */}
//         <div className="flex items-center justify-between border-b border-border px-6 py-4">
//           <div>
//             <h2 className="font-serif text-base font-semibold text-foreground">
//               {isEdit ? "Modifier la traduction" : "Ajouter une traduction"} — {langLabel}
//             </h2>
//             <p className="text-xs text-muted-foreground">
//               {isEdit ? "Modification de la traduction existante" : "Nouvelle traduction pour cet article"}
//             </p>
//           </div>
//           <button onClick={onClose} className="rounded-md p-1.5 hover:bg-surface text-muted-foreground hover:text-foreground transition-colors">
//             <X className="h-4 w-4" />
//           </button>
//         </div>

//         {/* Body */}
//         <div className="flex-1 overflow-y-auto p-6 space-y-4">
//           {error && (
//             <div className="flex items-center gap-2 rounded-md bg-alert/10 border border-alert/30 px-4 py-2 text-xs text-alert font-medium">
//               <AlertCircle className="h-3.5 w-3.5 shrink-0" />{error}
//             </div>
//           )}
//           <div className="space-y-1.5">
//             <label className="text-xs font-semibold text-muted-foreground">Titre <span className="text-alert">*</span></label>
//             <input
//               value={fields.titre}
//               onChange={(e) => set("titre", e.target.value)}
//               placeholder="Titre de l'article"
//               autoFocus
//               className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-foreground"
//             />
//           </div>
//           <div className="space-y-1.5">
//             <label className="text-xs font-semibold text-muted-foreground">Sous-titre</label>
//             <input
//               value={fields.sousTitre}
//               onChange={(e) => set("sousTitre", e.target.value)}
//               placeholder="Sous-titre ou chapô"
//               className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-foreground"
//             />
//           </div>
//           <div className="space-y-1.5">
//             <label className="text-xs font-semibold text-muted-foreground">Résumé</label>
//             <textarea
//               value={fields.resume}
//               onChange={(e) => set("resume", e.target.value)}
//               placeholder="Résumé court (SEO / partage)"
//               rows={3}
//               className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-foreground resize-none"
//             />
//           </div>
//           <div className="space-y-1.5">
//             <label className="text-xs font-semibold text-muted-foreground">Corps de l'article</label>
//             <textarea
//               value={fields.corps}
//               onChange={(e) => set("corps", e.target.value)}
//               placeholder="Contenu principal de l'article…"
//               rows={10}
//               className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-foreground resize-y font-mono"
//             />
//           </div>
//         </div>

//         {/* Footer */}
//         <div className="flex items-center justify-between border-t border-border px-6 py-4">
//           <button onClick={onClose} className="rounded-md border border-border px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-surface transition-colors">
//             Annuler
//           </button>
//           <button
//             onClick={handleSave}
//             disabled={saving}
//             className="flex items-center gap-2 rounded-md bg-foreground px-5 py-2 text-sm font-semibold text-background hover:bg-foreground/90 disabled:opacity-60 transition-colors"
//           >
//             {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
//             Enregistrer
//           </button>
//         </div>
//       </div>
//     </div>
//   );
// }

// // ─── Carte langue ─────────────────────────────────────────────────────────────
// function LangueCard({
//   lang,
//   trad,
//   articleId,
//   onRefresh,
// }: {
//   lang: typeof LANGUES[number];
//   trad?: TraductionArticleDto;
//   articleId: number;
//   onRefresh: () => void;
// }) {
//   const deleteTraduction = useDeleteTraductionArticle();
//   const [formOpen, setFormOpen] = useState(false);
//   const [expanded, setExpanded] = useState(false);
//   const [deleting, setDeleting] = useState(false);

//   const hasIt = !!trad;

//   const handleDelete = async () => {
//     if (!trad?.id) return;
//     setDeleting(true);
//     try {
//       await deleteTraduction.mutateAsync(trad.id);
//       onRefresh();
//     } finally {
//       setDeleting(false);
//     }
//   };

//   return (
//     <>
//       <div className="rounded-md border border-border bg-background overflow-hidden">
//         {/* En-tête carte */}
//         <div className="flex items-center gap-3 px-4 py-3">
//           <span className="text-xl leading-none">{lang.flag}</span>
//           <div className="flex-1 min-w-0">
//             <div className="flex items-center gap-2">
//               <span className="text-sm font-semibold text-foreground">{lang.label}</span>
//               {hasIt ? (
//                 <span className="inline-flex items-center gap-1 rounded-sm px-2 py-0.5 text-[10px] font-bold bg-verified/10 text-verified">
//                   <Check className="h-2.5 w-2.5" /> Traduit
//                 </span>
//               ) : (
//                 <span className="inline-flex items-center gap-1 rounded-sm px-2 py-0.5 text-[10px] font-bold bg-surface text-muted-foreground">
//                   <Minus className="h-2.5 w-2.5" /> Manquant
//                 </span>
//               )}
//             </div>
//             {trad?.titre && (
//               <p className="text-[11px] text-muted-foreground line-clamp-1 mt-0.5">{trad.titre}</p>
//             )}
//           </div>
//           <div className="flex items-center gap-1 shrink-0">
//             {hasIt ? (
//               <>
//                 <button
//                   onClick={() => setExpanded((p) => !p)}
//                   title="Voir le contenu"
//                   className="rounded p-1 text-muted-foreground hover:text-foreground hover:bg-surface transition-colors"
//                 >
//                   <ChevronDown className={`h-3.5 w-3.5 transition-transform ${expanded ? "rotate-180" : ""}`} />
//                 </button>
//                 <button
//                   onClick={() => setFormOpen(true)}
//                   title="Modifier"
//                   className="rounded p-1 text-muted-foreground hover:text-foreground hover:bg-surface transition-colors"
//                 >
//                   <Edit2 className="h-3.5 w-3.5" />
//                 </button>
//                 <button
//                   onClick={handleDelete}
//                   disabled={deleting}
//                   title="Supprimer cette traduction"
//                   className="rounded p-1 text-muted-foreground hover:text-alert hover:bg-alert/10 transition-colors"
//                 >
//                   {deleting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
//                 </button>
//               </>
//             ) : (
//               <button
//                 onClick={() => setFormOpen(true)}
//                 className="flex items-center gap-1 rounded-md border border-dashed border-border px-2.5 py-1 text-xs font-semibold text-muted-foreground hover:text-foreground hover:border-foreground transition-colors"
//               >
//                 <Plus className="h-3 w-3" /> Ajouter
//               </button>
//             )}
//           </div>
//         </div>

//         {/* Contenu déplié */}
//         {expanded && trad && (
//           <div className="border-t border-border bg-surface/30 px-4 py-4 space-y-3">
//             {trad.sousTitre && (
//               <div>
//                 <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">Sous-titre</p>
//                 <p className="text-sm text-foreground">{trad.sousTitre}</p>
//               </div>
//             )}
//             {trad.resume && (
//               <div>
//                 <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">Résumé</p>
//                 <p className="text-sm text-foreground">{trad.resume}</p>
//               </div>
//             )}
//             {trad.corps && (
//               <div>
//                 <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">Corps</p>
//                 <p className="text-sm text-foreground whitespace-pre-wrap font-mono text-[11px] leading-relaxed line-clamp-6">
//                   {trad.corps}
//                 </p>
//               </div>
//             )}
//             {!trad.sousTitre && !trad.resume && !trad.corps && (
//               <p className="text-xs text-muted-foreground italic">Seul le titre a été renseigné.</p>
//             )}
//           </div>
//         )}
//       </div>

//       {formOpen && (
//         <TraductionModal
//           articleId={articleId}
//           langue={lang.apiCode} // on envoie le code API majuscule
//           langLabel={lang.label}
//           initial={
//             trad
//               ? {
//                   titre: trad.titre || "",
//                   sousTitre: trad.sousTitre || "",
//                   resume: trad.resume || "",
//                   corps: trad.corps || "",
//                 }
//               : undefined
//           }
//           traductionId={trad?.id}
//           onClose={() => setFormOpen(false)}
//           onSaved={onRefresh}
//         />
//       )}
//     </>
//   );
// }

// // ─── Page Détail Article ──────────────────────────────────────────────────────
// function DetailArticle() {
//   const { id } = useParams<{ id: string }>();
//   const navigate = useNavigate();
//   const articleId = Number(id);

//   const { data: article, isLoading, refetch: refetchArticle } = useArticle(articleId);
//   const { data: traductions, refetch: refetchTraductions } = useTraductionsArticle(articleId);
//   const { data: categories } = useCategories();
//   const publishArticle = usePublishArticle();

//   const [publishing, setPublishing] = useState(false);

//   const handlePublish = async () => {
//     setPublishing(true);
//     try {
//       await publishArticle.mutateAsync(articleId);
//       refetchArticle();
//     } finally {
//       setPublishing(false);
//     }
//   };

//   const handleRefresh = () => {
//     refetchTraductions();
//     refetchArticle();
//   };

//   const categorie = categories?.find((c: any) => c.id === article?.categorieId);

//   if (isLoading) {
//     return (
//       <>
//         <AdminTopbar title="Détail article" subtitle="" />
//         <div className="flex-1 flex items-center justify-center gap-2 text-muted-foreground">
//           <Loader2 className="h-5 w-5 animate-spin" />
//           <span className="text-sm">Chargement…</span>
//         </div>
//       </>
//     );
//   }

//   if (!article) {
//     return (
//       <>
//         <AdminTopbar title="Article introuvable" subtitle="" />
//         <div className="flex-1 flex flex-col items-center justify-center gap-3 text-muted-foreground">
//           <AlertCircle className="h-8 w-8 opacity-40" />
//           <p className="text-sm">Cet article n'existe pas ou a été supprimé.</p>
//           <button onClick={() => navigate(-1)} className="text-xs font-semibold text-foreground underline">
//             Retour
//           </button>
//         </div>
//       </>
//     );
//   }

//   const titreDisplay = article.traductions?.[0]?.titre || article.slug;
//   // On fusionne les traductions de l'API dédiée avec celles embarquées dans l'article
//   const traductionsList: TraductionArticleDto[] = traductions || article.traductions || [];
//   const completion = Math.round(((article.languesDisponibles?.length || 0) / LANGUES.length) * 100);

//   return (
//     <>
//       <AdminTopbar
//         title={titreDisplay}
//         subtitle={`Article · ${statutLabel}`}
//       />

//       <div className="flex-1 overflow-y-auto p-6 space-y-6">
//         {/* Navigation + actions */}
//         <div className="flex items-center justify-between">
//           <button
//             onClick={() => navigate(-1)}
//             className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground hover:text-foreground transition-colors"
//           >
//             <ArrowLeft className="h-3.5 w-3.5" />
//             Retour aux articles
//           </button>
//           {article.statut !== "PUBLIE" && (
//             <button
//               onClick={handlePublish}
//               disabled={publishing}
//               className="flex items-center gap-1.5 rounded-md bg-verified px-3 py-2 text-xs font-semibold text-white hover:bg-verified/90 disabled:opacity-60 transition-colors"
//             >
//               {publishing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
//               Publier
//             </button>
//           )}
//         </div>

//         {/* Métadonnées */}
//         <ModulePanel title="Métadonnées">
//           <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
//             <div>
//               <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">Statut</p>
//               <EditorialStatusBadge status={statutLabel} />
//             </div>
//             <div>
//               <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">Type</p>
//               <span className="font-mono text-sm text-foreground">{article.type}</span>
//             </div>
//             <div>
//               <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">Rubrique</p>
//               <span className="text-sm text-foreground">{categorie?.libelleFr || `#${article.categorieId}`}</span>
//             </div>
//             <div>
//               <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">Signature</p>
//               <span className="text-sm text-foreground">{article.signature || "—"}</span>
//             </div>
//             <div>
//               <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">Slug</p>
//               <span className="font-mono text-xs text-muted-foreground">{article.slug}</span>
//             </div>
//             <div>
//               <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">Temps de lecture</p>
//               <span className="text-sm text-foreground">{article.tempsLecture ? `${article.tempsLecture} min` : "—"}</span>
//             </div>
//             <div>
//               <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">Publié le</p>
//               <span className="text-sm text-foreground">
//                 {article.publieLe ? new Date(article.publieLe).toLocaleDateString("fr-FR") : "—"}
//               </span>
//             </div>
//             <div>
//               <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">Modifié le</p>
//               <span className="text-sm text-foreground">
//                 {article.modifieLe ? new Date(article.modifieLe).toLocaleDateString("fr-FR") : "—"}
//               </span>
//             </div>
//           </div>

//           {article.etiquettes && article.etiquettes.length > 0 && (
//             <div className="mt-4 pt-4 border-t border-border">
//               <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-2">Étiquettes</p>
//               <div className="flex flex-wrap gap-1.5">
//                 {article.etiquettes.map((e: any) => (
//                   <span key={e.id} className="rounded-full border border-border px-2.5 py-0.5 text-xs font-medium text-muted-foreground">
//                     {e.libelleFr}
//                   </span>
//                 ))}
//               </div>
//             </div>
//           )}
//         </ModulePanel>

//         {/* Traductions */}
//         <ModulePanel
//           title="Traductions"
//           description={`${article.languesDisponibles?.length || 0} / ${LANGUES.length} langues traduites — ${completion}% de complétion`}
//         >
//           {/* Barre de progression */}
//           <div className="mb-5">
//             <div className="flex items-center justify-between mb-1.5">
//               <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1">
//                 <Globe className="h-3 w-3" /> Complétion multilingue
//               </span>
//               <span className="text-[10px] font-bold text-muted-foreground">{completion}%</span>
//             </div>
//             <div className="h-1.5 rounded-full bg-surface overflow-hidden">
//               <div
//                 className="h-full rounded-full bg-verified transition-all duration-500"
//                 style={{ width: `${completion}%` }}
//               />
//             </div>
//           </div>

//           {/* Une carte par langue */}
//           <div className="space-y-2">
//             {LANGUES.map((lang) => {
//               // Matching : la traduction peut avoir langue "FR", "fr", etc.
//               const trad = traductionsList.find(
//                 (t) => t.langue?.toLowerCase() === lang.code.toLowerCase()
//               );
//               return (
//                 <LangueCard
//                   key={lang.code}
//                   lang={lang}
//                   trad={trad}
//                   articleId={articleId}
//                   onRefresh={handleRefresh}
//                 />
//               );
//             })}
//           </div>
//         </ModulePanel>
//       </div>
//     </>
//   );
// }

// export default DetailArticle;
